import { useState } from "react";
import { Box, Container, Typography, Link, Table, TableBody, TableCell, TableHead, TableRow } from "@mui/material";
import IconButton from "@mui/material/IconButton";
import ArrowBackIcon from "@mui/icons-material/ArrowBack";
import { useNavigate } from "react-router-dom";
import ContactModal from "../landing/ContactModal";

const GOLD      = "#D97706";

const Section = ({ title, children }) => (
  <Box sx={{ mb: 5 }}>
    <Typography sx={{ fontSize: 18, fontWeight: 700, color: "#0F172A", mb: 2, fontFamily: "IBM Plex Sans, sans-serif", letterSpacing: "-.3px" }}>
      {title}
    </Typography>
    {children}
  </Box>
);

const P = ({ children }) => (
  <Typography sx={{ fontSize: 15, color: "#475569", lineHeight: 1.85, mb: 2, fontFamily: "IBM Plex Sans, sans-serif" }}>
    {children}
  </Typography>
);

const Li = ({ children }) => (
  <Box sx={{ display: "flex", gap: 1.5, mb: 1.25 }}>
    <Box sx={{ width: 5, height: 5, borderRadius: "50%", background: GOLD, mt: "10px", flexShrink: 0 }} />
    <Typography sx={{ fontSize: 15, color: "#475569", lineHeight: 1.8, fontFamily: "IBM Plex Sans, sans-serif" }}>{children}</Typography>
  </Box>
);

const COOKIES = [
  { name: "aml_session",      type: "Strictly Necessary", duration: "Session",   purpose: "Maintains your authenticated session. Deleted when you close the browser." },
  { name: "aml_remember",     type: "Strictly Necessary", duration: "30 days",   purpose: "Keeps you logged in across browser sessions when you select 'Remember me'." },
  { name: "XSRF-TOKEN",       type: "Strictly Necessary", duration: "Session",   purpose: "Cross-site request forgery protection. Required for form submissions." },
  { name: "aml_prefs",        type: "Functional",         duration: "1 year",    purpose: "Stores your interface preferences (language, theme, sidebar state)." },
  { name: "_ga",              type: "Analytics",          duration: "2 years",   purpose: "Google Analytics — distinguishes unique users for aggregate usage statistics." },
  { name: "_ga_*",            type: "Analytics",          duration: "2 years",   purpose: "Google Analytics — persists session state across page requests." },
  { name: "_gid",             type: "Analytics",          duration: "24 hours",  purpose: "Google Analytics — distinguishes users within a 24-hour window." },
];

export default function CookiePolicy() {
  const navigate = useNavigate();
  const [modalOpen, setModalOpen] = useState(false);
  return (
    <Box sx={{ minHeight: "100vh", background: "#fff", fontFamily: "IBM Plex Sans, sans-serif" }}>
      <ContactModal open={modalOpen} onClose={() => setModalOpen(false)} />

      {/* Header */}
      <Box sx={{ background: "#0F172A", py: 5, borderBottom: "1px solid rgba(255,255,255,.06)" }}>
        <Container maxWidth="lg">
          <Box sx={{ display: "flex", alignItems: "center", gap: 2, mb: 4 }}>
            <IconButton onClick={() => navigate("/")} sx={{ color: "#64748B", "&:hover": { color: "#fff" }, p: .5 }}>
              <ArrowBackIcon sx={{ fontSize: 20 }} />
            </IconButton>
            <Box component="img" src="/logo.png" alt="AMLDiligence" sx={{ height: 34, display: "block", filter: "brightness(0) invert(1)" }} />
          </Box>
          <Typography sx={{ fontSize: { xs: 28, md: 38 }, fontWeight: 700, color: "#F1F5F9", letterSpacing: "-1px", fontFamily: "IBM Plex Sans, sans-serif", mb: 1 }}>
            Cookie Policy
          </Typography>
          <Typography sx={{ fontSize: 14, color: "#64748B", fontFamily: "IBM Plex Sans, sans-serif" }}>
            Last updated: June 2025 · Effective date: June 1, 2025
          </Typography>
        </Container>
      </Box>

      {/* Body */}
      <Container maxWidth="lg" sx={{ py: { xs: 6, md: 10 } }}>
        <Box sx={{ maxWidth: 800 }}>

          <P>
            This Cookie Policy explains what cookies are, how AMLDiligence (a product of ZAHADS Chartered Accountants LLC) uses them on our website and platform at amldiligence.com, and your choices regarding cookies.
          </P>

          <Section title="1. What Are Cookies?">
            <P>
              Cookies are small text files placed on your device (computer, tablet, or smartphone) when you visit a website. They are widely used to make websites work efficiently, provide session management, and give website owners information about how their site is used.
            </P>
            <P>
              Cookies set by the website owner (us) are called "first-party cookies". Cookies set by parties other than the website owner are called "third-party cookies". Third-party cookies enable features or functionality provided by those third parties, such as analytics.
            </P>
          </Section>

          <Section title="2. Categories of Cookies We Use">

            <Box sx={{ mb: 3.5 }}>
              <Typography sx={{ fontSize: 15, fontWeight: 600, color: "#0F172A", mb: 1, fontFamily: "IBM Plex Sans, sans-serif" }}>Strictly Necessary Cookies</Typography>
              <P>These cookies are essential for the Platform to function and cannot be switched off. They include session authentication, CSRF protection, and load balancing. These cookies do not require your consent under applicable law.</P>
            </Box>

            <Box sx={{ mb: 3.5 }}>
              <Typography sx={{ fontSize: 15, fontWeight: 600, color: "#0F172A", mb: 1, fontFamily: "IBM Plex Sans, sans-serif" }}>Functional Cookies</Typography>
              <P>Functional cookies allow the Platform to remember choices you have made (such as interface preferences and sidebar state) to provide a more personalised experience. Disabling these may affect some features.</P>
            </Box>

            <Box sx={{ mb: 3.5 }}>
              <Typography sx={{ fontSize: 15, fontWeight: 600, color: "#0F172A", mb: 1, fontFamily: "IBM Plex Sans, sans-serif" }}>Analytics Cookies</Typography>
              <P>We use Google Analytics to understand how visitors interact with our website. Analytics cookies collect information in aggregate and anonymous form about page visits, session duration, and feature usage. This data helps us improve the Platform. Analytics cookies require your consent where applicable.</P>
            </Box>

            <Box sx={{ mb: 3.5 }}>
              <Typography sx={{ fontSize: 15, fontWeight: 600, color: "#0F172A", mb: 1, fontFamily: "IBM Plex Sans, sans-serif" }}>Marketing / Targeting Cookies</Typography>
              <P>Currently, we do not use marketing or advertising cookies on the Platform. If this changes, we will update this policy and obtain appropriate consent.</P>
            </Box>
          </Section>

          <Section title="3. Cookies We Set">
            <Box sx={{ overflowX: "auto", borderRadius: "12px", border: "1px solid #E2E8F0" }}>
              <Table size="small">
                <TableHead>
                  <TableRow sx={{ background: "#F8FAFC" }}>
                    {["Cookie Name", "Category", "Duration", "Purpose"].map(h => (
                      <TableCell key={h} sx={{ fontFamily: "IBM Plex Sans, sans-serif", fontSize: 12, fontWeight: 700, color: "#0F172A", letterSpacing: ".3px", textTransform: "uppercase", borderColor: "#E2E8F0" }}>
                        {h}
                      </TableCell>
                    ))}
                  </TableRow>
                </TableHead>
                <TableBody>
                  {COOKIES.map((c, i) => (
                    <TableRow key={c.name} sx={{ background: i % 2 === 0 ? "#fff" : "#FAFAFA" }}>
                      <TableCell sx={{ fontFamily: "IBM Plex Sans, sans-serif", fontSize: 13, color: "#0F172A", fontWeight: 600, borderColor: "#F1F5F9", whiteSpace: "nowrap" }}>{c.name}</TableCell>
                      <TableCell sx={{ fontFamily: "IBM Plex Sans, sans-serif", fontSize: 13, color: "#475569", borderColor: "#F1F5F9" }}>
                        <Box component="span" sx={{ px: 1.25, py: .4, borderRadius: "6px", fontSize: 11, fontWeight: 600, background: c.type === "Strictly Necessary" ? "rgba(22,163,74,.08)" : c.type === "Functional" ? "rgba(37,99,235,.08)" : "rgba(217,119,6,.08)", color: c.type === "Strictly Necessary" ? "#16A34A" : c.type === "Functional" ? "#2563EB" : GOLD }}>
                          {c.type}
                        </Box>
                      </TableCell>
                      <TableCell sx={{ fontFamily: "IBM Plex Sans, sans-serif", fontSize: 13, color: "#475569", borderColor: "#F1F5F9", whiteSpace: "nowrap" }}>{c.duration}</TableCell>
                      <TableCell sx={{ fontFamily: "IBM Plex Sans, sans-serif", fontSize: 13, color: "#475569", borderColor: "#F1F5F9" }}>{c.purpose}</TableCell>
                    </TableRow>
                  ))}
                </TableBody>
              </Table>
            </Box>
          </Section>

          <Section title="4. How to Control Cookies">
            <P>
              You can control and manage cookies in several ways:
            </P>

            <Box sx={{ mb: 3 }}>
              <Typography sx={{ fontSize: 15, fontWeight: 600, color: "#0F172A", mb: 1, fontFamily: "IBM Plex Sans, sans-serif" }}>Browser Settings</Typography>
              <P>Most browsers allow you to refuse or delete cookies through their settings. The following links provide instructions for common browsers:</P>
              <Li><Link href="https://support.google.com/chrome/answer/95647" target="_blank" rel="noopener noreferrer" sx={{ color: GOLD }}>Google Chrome</Link></Li>
              <Li><Link href="https://support.mozilla.org/en-US/kb/enhanced-tracking-protection-firefox-desktop" target="_blank" rel="noopener noreferrer" sx={{ color: GOLD }}>Mozilla Firefox</Link></Li>
              <Li><Link href="https://support.apple.com/en-gb/guide/safari/sfri11471/mac" target="_blank" rel="noopener noreferrer" sx={{ color: GOLD }}>Apple Safari</Link></Li>
              <Li><Link href="https://support.microsoft.com/en-us/microsoft-edge/delete-cookies-in-microsoft-edge-63947406-40ac-c3b8-57b9-2a946a29ae09" target="_blank" rel="noopener noreferrer" sx={{ color: GOLD }}>Microsoft Edge</Link></Li>
            </Box>

            <Box sx={{ mb: 2 }}>
              <Typography sx={{ fontSize: 15, fontWeight: 600, color: "#0F172A", mb: 1, fontFamily: "IBM Plex Sans, sans-serif" }}>Google Analytics Opt-Out</Typography>
              <P>To opt out of Google Analytics tracking across all websites, you can install the <Link href="https://tools.google.com/dlpage/gaoptout" target="_blank" rel="noopener noreferrer" sx={{ color: GOLD }}>Google Analytics Opt-out Browser Add-on</Link>.</P>
            </Box>

            <Box sx={{ p: 2.5, borderRadius: "10px", background: "rgba(217,119,6,.05)", border: "1px solid rgba(217,119,6,.15)" }}>
              <Typography sx={{ fontSize: 14, color: "#475569", fontFamily: "IBM Plex Sans, sans-serif", lineHeight: 1.75 }}>
                <strong style={{ color: "#0F172A" }}>Please note:</strong> Disabling strictly necessary cookies will prevent you from logging in and using the Platform. Disabling functional cookies may mean your preferences are not saved between sessions.
              </Typography>
            </Box>
          </Section>

          <Section title="5. Cookie Consent">
            <P>
              When you first visit our website, we display a cookie consent banner. Strictly necessary cookies are activated automatically as they are essential for operation. Functional and analytics cookies are only set if you accept them. You may withdraw or change your consent at any time by clearing your browser cookies and revisiting the site.
            </P>
          </Section>

          <Section title="6. Third-Party Cookies">
            <P>
              Third-party services embedded in our website (such as Google Analytics) may set their own cookies. We have no direct control over these cookies. Please refer to the relevant third-party privacy policies for further information.
            </P>
          </Section>

          <Section title="7. Updates to This Policy">
            <P>
              We may update this Cookie Policy from time to time to reflect changes in technology, law, or our business practices. The "Last updated" date at the top of this page will be revised accordingly.
            </P>
          </Section>

          <Section title="8. Contact Us">
            <Box sx={{ background: "#F8FAFC", border: "1px solid #E2E8F0", borderRadius: "12px", p: 3 }}>
              <Typography sx={{ fontSize: 14, color: "#475569", lineHeight: 2, fontFamily: "IBM Plex Sans, sans-serif" }}>
                <strong style={{ color: "#0F172A" }}>AMLDiligence / ZAHADS Chartered Accountants LLC</strong><br />
                Email: <Link component="button" onClick={() => setModalOpen(true)} sx={{ color: GOLD, cursor: "pointer", fontFamily: "IBM Plex Sans, sans-serif", fontSize: 14, background: "none", border: "none", padding: 0, verticalAlign: "baseline" }}>Contact us</Link><br />
                Phone: <Link href="tel:+97144519930" sx={{ color: GOLD }}>+971 (4) 451 9930</Link>
              </Typography>
            </Box>
          </Section>

        </Box>
      </Container>

      {/* Footer bar */}
      <Box sx={{ background: "#0F172A", py: 3, borderTop: "1px solid rgba(255,255,255,.06)" }}>
        <Container maxWidth="lg">
          <Box sx={{ display: "flex", flexWrap: "wrap", gap: 3, justifyContent: "space-between", alignItems: "center" }}>
            <Typography sx={{ fontSize: 12, color: "#4B5563", fontFamily: "IBM Plex Sans, sans-serif" }}>
              © {new Date().getFullYear()} AMLDiligence.com · ZAHADS Chartered Accountants. All rights reserved.
            </Typography>
            <Box sx={{ display: "flex", gap: 3 }}>
              {[["Privacy Policy", "/privacy-policy"], ["Terms of Service", "/terms-of-service"], ["Cookie Policy", "/cookie-policy"]].map(([label, href]) => (
                <Link key={label} href={href} underline="none" sx={{ fontSize: 12, color: "#4B5563", fontFamily: "IBM Plex Sans, sans-serif", "&:hover": { color: "#9CA3AF" } }}>{label}</Link>
              ))}
            </Box>
          </Box>
        </Container>
      </Box>
    </Box>
  );
}
